import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, Form, Row, Col, Upload, message } from 'antd';
import dayjs from 'dayjs';
import { AntdButton, InputField } from '../../../blueplanitUtils/InputField';
import { setLoader } from '../../../Slices/loaderSlice';
import { updateAsset } from '../../../Slices/assetSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { S3ACCESSURL } from '../../../databucket';

const EditWarrantyModal = ({ 
    isOpen, 
    onClose, 
    selectedAsset, 
    setSelectedAsset,
    onSuccess
}) => {
    const [editForm] = Form.useForm();
    const [warrantyFiles, setWarrantyFiles] = useState([]);
    const [originalEditValues, setOriginalEditValues] = useState(null);
    const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
    const dispatch = useDispatch();
    const isEditMode = true;

    useEffect(() => {
        if (isOpen && selectedAsset) {
            setWarrantyFiles([]);
            
            const originalValues = {
                warrantyDuration: selectedAsset.warrantyDuration,
                warrantyAttachments: selectedAsset.warrantyAttachments || []
            };
            setOriginalEditValues(originalValues);

            editForm.setFieldsValue({
                ...selectedAsset,
                purchaseDate: selectedAsset.purchaseDate ? dayjs(parseInt(selectedAsset.purchaseDate)) : null,
            });
        }
    }, [isOpen, selectedAsset, editForm]);

    const handleBeforeUpload = async (file) => {
        try {
            const base64 = await convertToBase64(file);
            const attachment = {
                filename: file.name,
                data: base64,
                mimetype: file.type,
                userId: loginUser?.id || '',
                emailId: loginUser?.email || '',
            };
            setWarrantyFiles(prev => [...prev, attachment]);
            return false; 
        } catch (error) {
            message.error('Failed to process file');
            return false;
        }
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleDeleteAtta = (file, index) => {
        setSelectedAsset(prev => ({
            ...prev,
            warrantyAttachments: prev.warrantyAttachments.map(att =>
                att.filename === file.filename && att.data === file.data
                    ? { ...att, status: false }
                    : att
            )
        }));
    };

    const hasChangesInEdit = (currentValues, currentWarrantyFiles) => {
        if (!originalEditValues) return true;

        const currentDuration = !isNaN(parseInt(currentValues.warrantyDuration)) && parseInt(currentValues.warrantyDuration) > 0
            ? parseInt(currentValues.warrantyDuration)
            : null;
        const originalDuration = originalEditValues.warrantyDuration;

        if (currentDuration !== originalDuration) {
            return true;
        }

        if (currentWarrantyFiles && currentWarrantyFiles.length > 0) {
            return true;
        }

        const originalActiveAttachments = originalEditValues.warrantyAttachments?.filter(att => att.status !== false) || [];
        const currentActiveAttachments = selectedAsset?.warrantyAttachments?.filter(att => att.status !== false) || [];

        if (originalActiveAttachments.length !== currentActiveAttachments.length) {
            return true;
        }

        return false;
    };

    const handleCancel = () => {
        if (originalEditValues?.warrantyAttachments) {
            setSelectedAsset(prev => ({
                ...prev,
                warrantyAttachments: originalEditValues.warrantyAttachments
            }));
        }
        setWarrantyFiles([]);
        setOriginalEditValues(null);
        onClose();
    };

    const handleUpdate = () => {
        editForm.validateFields().then(async (values) => {
            if (!hasChangesInEdit(values, warrantyFiles)) {
                message.error("No Change Detected to Update");
                return;
            }

            dispatch(setLoader(true));
            try {
                const parsedDuration = !isNaN(parseInt(values.warrantyDuration)) && parseInt(values.warrantyDuration) > 0
                    ? parseInt(values.warrantyDuration)
                    : null;

                const updatedExistingAttachments = selectedAsset.warrantyAttachments.map(att => {
                    const cleanedAtt = {
                        emailId: att.id || att._id, // Use the attachment's ID as emailId for backend matching
                        filename: att.filename,
                        data: att.data,
                        userId: att.userId,
                        mimetype: att.mimetype,
                        addDate: att.addDate,
                        updateDate: att.updateDate,
                        status: att.status,
                        userName: att.userName,
                        deleteDate: att.deleteDate || null
                    };

                    if (att.status === false && originalEditValues.warrantyAttachments?.some(orig =>
                        orig.filename === att.filename && orig.data === att.data && orig.status !== false)) {
                        cleanedAtt.status = false;
                        cleanedAtt.deleteDate = new Date().toISOString();
                    }

                    if (att.attachmetHistory && Array.isArray(att.attachmetHistory)) {
                        cleanedAtt.attachmetHistory = att.attachmetHistory.map(history => ({
                            userId: history.userId,
                            emailId: history.emailId,
                            userName: history.userName,
                            replacedFile: history.replacedFile,
                            data: history.data,
                            filename: history.filename,
                            mimetype: history.mimetype,
                            deleteDate: history.deleteDate,
                            addDate: history.addDate,
                            updateDate: history.updateDate,
                            status: history.status
                        }));
                    }

                    return cleanedAtt;
                });

                const newWarrantyAttachments = warrantyFiles.length > 0 ? warrantyFiles.map(file => ({
                    userId: loginUser?._id,
                    userName: `${loginUser?.firstname} ${loginUser?.lastname}`,
                    emailId: null, 
                    filename: file.name,
                    data: file.url || file.response?.url || file.data,
                    mimetype: file.type,
                    status: true,
                    addDate: new Date().toISOString(),
                    updateDate: new Date().toISOString(),
                    deleteDate: null,
                    replacedFile: null
                })) : [];

                const allWarrantyAttachments = [...updatedExistingAttachments, ...newWarrantyAttachments];

                const updateVariables = {
                    id: selectedAsset.id,
                    editedBy: `${loginUser?.firstname} ${loginUser?.lastname}`,
                    editedAt: new Date().toISOString(),
                };

                const currentDuration = !isNaN(parseInt(values.warrantyDuration)) && parseInt(values.warrantyDuration) > 0
                    ? parseInt(values.warrantyDuration)
                    : null;
                const originalDuration = originalEditValues.warrantyDuration;

                if (currentDuration !== originalDuration) {
                    updateVariables.warrantyDuration = parsedDuration;
                }

                const originalActiveAttachments = originalEditValues.warrantyAttachments?.filter(att => att.status !== false) || [];
                const currentActiveAttachments = selectedAsset?.warrantyAttachments?.filter(att => att.status !== false) || [];
                const hasAttachmentChanges = originalActiveAttachments.length !== currentActiveAttachments.length || newWarrantyAttachments.length > 0;

                if (hasAttachmentChanges) {
                    updateVariables.warrantyAttachments = allWarrantyAttachments;
                }

                if (values.purchaseDate && selectedAsset.purchaseDate) {
                    const currentPurchaseDate = dayjs.isDayjs(values.purchaseDate)
                        ? values.purchaseDate.valueOf().toString()
                        : dayjs(values.purchaseDate).valueOf().toString();

                    if (currentPurchaseDate !== selectedAsset.purchaseDate) {
                        updateVariables.purchaseDate = currentPurchaseDate;
                    }
                }

                const result = await dispatch(updateAsset(updateVariables));

                if (updateAsset.fulfilled.match(result)) {
                    setWarrantyFiles([]);
                    setOriginalEditValues(null);
                    message.success('Asset updated successfully!');
                    onSuccess();
                    onClose();
                } else {
                    message.error('Update failed!');
                }

            } catch (error) {
                message.error('Failed to update asset');
            } finally {
                dispatch(setLoader(false));
            }
        });
    };

    return (
        <Modal
            title="Edit Warranty"
            okText="Update"
            width={600}
            open={isOpen}
            onCancel={handleCancel}
            onOk={handleUpdate}
        >
            <Form layout="vertical" form={editForm}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="assetTagId" label="Asset Tag ID">
                            <InputField disabled={isEditMode} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="productName" label="Product Name">
                            <InputField disabled={isEditMode} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="warrantyDuration" label="Warranty in Months">
                            <InputField />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="warrantyAttachments"
                            label="Warranty Attachments"
                        >
                            <div>
                                {selectedAsset?.warrantyAttachments &&
                                    selectedAsset.warrantyAttachments.filter(att => att.status !== false).length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 8,
                                                    marginTop: '2px',
                                                    padding: '8px',
                                                    backgroundColor: '#f9f9f9',
                                                    borderRadius: '4px',
                                                }}
                                            >
                                                {selectedAsset.warrantyAttachments
                                                    .filter(att => att.status !== false)
                                                    .map((file, index) => (
                                                        <div
                                                            key={`${file.filename}-${index}`}
                                                            style={{
                                                                padding: '4px 8px',
                                                                backgroundColor: '#e6f7ff',
                                                                borderRadius: '4px',
                                                                fontSize: '12px',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <a
                                                                href={S3ACCESSURL + file.data}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#1890ff', flex: 1 }}
                                                            >
                                                                {file.filename}
                                                            </a>
                                                            <FontAwesomeIcon icon={faTrash}
                                                                style={{ color: 'red', cursor: 'pointer', marginLeft: 8 }}
                                                                onClick={() => handleDeleteAtta(file, index)}
                                                            />
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    )}

                                <div>
                                    <Upload
                                        beforeUpload={handleBeforeUpload}
                                        showUploadList
                                        multiple
                                        fileList={warrantyFiles}
                                        onChange={({ fileList }) => setWarrantyFiles(fileList)}
                                        itemRender={(originNode, file, fileList) => (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                                <span style={{ fontSize: '12px' }}>{file.name}</span>
                                                <FontAwesomeIcon icon={faTrash}
                                                    style={{ color: 'red', cursor: 'pointer' }}
                                                    onClick={() => {
                                                        const newList = fileList.filter(f => f.uid !== file.uid);
                                                        setWarrantyFiles(newList);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    >
                                        <AntdButton
                                            label="New Warranty"
                                            icon={<FontAwesomeIcon icon={faUpload} />}
                                            type="primary"
                                            style={{
                                                marginTop: '3px',
                                                borderRadius: '10px',
                                                fontWeight: 500,
                                            }}
                                        />
                                    </Upload>
                                </div>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default EditWarrantyModal;
